// Does the table fit, or must it be tiles? Measured, not declared.
//
// The deal register switches at a breakpoint it can name, because ten known columns have a
// natural minimum it can state. The tracker's columns come from the API and a leg may carry
// twenty of them, so the only honest answer is to lay the table out and look: if its content is
// wider than the box it is in, the page would need a horizontal scrollbar, and the rule is that
// it never has one. The table is tried first for every page of data, measured before paint, and
// replaced with tiles if it overflows; the width it needed is remembered, and when the box grows
// back to that width the table is tried again.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export default function useTableFits(data) {
  // The element whose width is the space available, present in both presentations.
  const sectionRef = useRef(null);
  // The table's scroll box, present only while the table is.
  const scrollRef = useRef(null);
  const needed = useRef(0);
  const [tiles, setTiles] = useState(false);

  // New data may be narrower than the last: try the table again.
  useLayoutEffect(() => {
    setTiles(false);
  }, [data]);

  // Before paint, so a table that is about to be replaced is never seen.
  useLayoutEffect(() => {
    const box = scrollRef.current;
    if (box && box.scrollWidth > box.clientWidth) {
      needed.current = box.scrollWidth;
      setTiles(true);
    }
  }, [data, tiles]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const observer = new ResizeObserver(() => {
      const box = scrollRef.current;
      if (box) {
        if (box.scrollWidth > box.clientWidth) {
          needed.current = box.scrollWidth;
          setTiles(true);
        }
      } else if (section.clientWidth >= needed.current) {
        setTiles(false);
      }
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return { tiles, sectionRef, scrollRef };
}
